import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  FormHelperText
} from '@mui/material';
import { Email as EmailIcon, Person as PersonIcon, Message as MessageIcon } from '@mui/icons-material';
import axios from 'axios';

/**
 * InviteMaidDialog - Admin interface for sending maid invitation emails
 * Provides a form for admins to send professional invitation emails to potential maid applicants
 */
const InviteMaidDialog = ({ open, onClose, onInvitationSent }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    personalMessage: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  // Form validation
  const validateForm = () => {
    const errors = {};
    
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (formData.personalMessage && formData.personalMessage.length > 500) {
      errors.personalMessage = 'Personal message must be less than 500 characters';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle input changes
  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
    
    // Clear general error and success messages
    setError('');
    setSuccess('');
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/maids/invite`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setSuccess(`Invitation sent successfully to ${formData.firstName} ${formData.lastName}!`);
        
        // Call callback if provided
        if (onInvitationSent) {
          onInvitationSent({
            ...formData,
            messageId: response.data.data.messageId,
            sentAt: response.data.data.sentAt
          });
        }

        // Reset form after short delay
        setTimeout(() => {
          handleClose();
        }, 2000);
      }
    } catch (err) {
      console.error('Error sending invitation:', err);
      
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.response?.status === 400) {
        setError('Invalid form data. Please check your inputs and try again.');
      } else if (err.response?.status === 403) {
        setError('You do not have permission to send invitations.');
      } else if (err.response?.status === 500) {
        setError('Email service is currently unavailable. Please try again later.');
      } else {
        setError('Failed to send invitation. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle dialog close
  const handleClose = () => {
    if (!loading) {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        personalMessage: ''
      });
      setValidationErrors({});
      setError('');
      setSuccess('');
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      aria-labelledby="invite-maid-dialog-title"
    >
      <DialogTitle id="invite-maid-dialog-title">
        <Box display="flex" alignItems="center" gap={1}>
          <EmailIcon color="primary" />
          <Typography variant="h6" component="span">
            Invite New Technician
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Send a professional invitation email to a potential cleaning professional
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box component="form" noValidate>
          {/* Personal Information Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" color="primary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon fontSize="small" />
              Personal Information
            </Typography>
            
            <Box display="flex" gap={2} sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={handleInputChange('firstName')}
                error={!!validationErrors.firstName}
                helperText={validationErrors.firstName}
                disabled={loading}
                required
                autoFocus
              />
              
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={handleInputChange('lastName')}
                error={!!validationErrors.lastName}
                helperText={validationErrors.lastName}
                disabled={loading}
                required
              />
            </Box>
            
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={handleInputChange('email')}
              error={!!validationErrors.email}
              helperText={validationErrors.email || 'A professional invitation will be sent to this email'}
              disabled={loading}
              required
              sx={{ mb: 2 }}
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Personal Message Section */}
          <Box>
            <Typography variant="subtitle1" color="primary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <MessageIcon fontSize="small" />
              Personal Message (Optional)
            </Typography>
            
            <TextField
              fullWidth
              label="Personal Message"
              multiline
              rows={3}
              value={formData.personalMessage}
              onChange={handleInputChange('personalMessage')}
              error={!!validationErrors.personalMessage}
              helperText={validationErrors.personalMessage || 'Add a personal touch to the invitation (max 500 characters)'}
              disabled={loading}
              placeholder="We were impressed by your experience and would love to have you join our team..."
            />
            
            <FormHelperText sx={{ textAlign: 'right', mt: 1 }}>
              {formData.personalMessage.length}/500 characters
            </FormHelperText>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={handleClose} 
          disabled={loading}
          color="inherit"
        >
          Cancel
        </Button>
        
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <EmailIcon />}
          sx={{ minWidth: 140 }}
        >
          {loading ? 'Sending...' : 'Send Invitation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InviteMaidDialog;